import { useState } from "react";
import { CATEGORY_META } from "../../../mocks/allVenues";
import { allVenues } from "../../../mocks/allVenues";
import CategoryFormModal from "../components/CategoryFormModal";
import ConfirmModal from "../components/ConfirmModal";

interface CategoryEntry {
  slug: string;
  title: string;
  desc: string;
  icon: string;
  count: string;
  image: string;
}

const INITIAL_CATEGORIES: CategoryEntry[] = Object.entries(CATEGORY_META).map(([slug, meta]) => ({
  slug,
  title: meta.title,
  desc: meta.desc,
  icon: meta.icon,
  count: meta.count,
  image: meta.image,
}));

export default function CategoriesSection() {
  const [categories, setCategories] = useState<CategoryEntry[]>(INITIAL_CATEGORIES);

  type ModalState =
    | { type: "none" }
    | { type: "add" }
    | { type: "edit"; cat: CategoryEntry }
    | { type: "delete"; cat: CategoryEntry };

  const [modal, setModal] = useState<ModalState>({ type: "none" });

  const getVenueCount = (slug: string) => {
    if (slug === "featured") return allVenues.filter((v) => v.isEditorPick || v.isSponsored).length;
    return allVenues.filter((v) => v.categorySlug === slug).length;
  };

  const handleAdd = (data: { title: string; desc: string; slug: string; icon: string; count: string; image?: string }) => {
    if (categories.some((c) => c.slug === data.slug)) return;
    setCategories((prev) => [...prev, { ...data, image: data.image ?? "" }]);
    setModal({ type: "none" });
  };

  const handleEdit = (data: { title: string; desc: string; slug: string; icon: string; count: string; image?: string }) => {
    if (modal.type !== "edit") return;
    setCategories((prev) => prev.map((c) =>
      c.slug === modal.cat.slug ? { ...c, ...data } : c
    ));
    setModal({ type: "none" });
  };

  const handleDelete = () => {
    if (modal.type !== "delete") return;
    setCategories((prev) => prev.filter((c) => c.slug !== modal.cat.slug));
    setModal({ type: "none" });
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-stone-800">Kategori Yönetimi</h2>
          <p className="text-sm text-stone-400 mt-0.5">{categories.length} kategori</p>
        </div>
        <button
          onClick={() => setModal({ type: "add" })}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" /> Kategori Ekle
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {categories.map((cat) => {
          const count = getVenueCount(cat.slug);
          return (
            <div key={cat.slug} className="bg-white rounded-2xl overflow-hidden border border-stone-100 group hover:border-stone-200 transition-colors">
              {/* Cover */}
              <div className="relative h-32 overflow-hidden bg-stone-100">
                {cat.image ? (
                  <img src={cat.image} alt={cat.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-stone-100">
                    <i className={`${cat.icon} text-4xl text-stone-300`} />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg">
                    <i className={`${cat.icon} text-white text-sm`} />
                  </div>
                  <h3 className="text-white font-bold text-sm">{cat.title}</h3>
                </div>
                <div className="absolute top-3 right-3 px-2.5 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs font-semibold">
                  {count} mekan
                </div>
              </div>

              {/* Body */}
              <div className="p-4">
                <p className="text-xs text-stone-500 leading-relaxed mb-3 line-clamp-2">{cat.desc}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-stone-400">Slug:</span>
                    <code className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-md">/{cat.slug}</code>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setModal({ type: "edit", cat })}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
                      title="Düzenle"
                    >
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button
                      onClick={() => setModal({ type: "delete", cat })}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      title="Sil"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Empty add card */}
        <button
          onClick={() => setModal({ type: "add" })}
          className="border-2 border-dashed border-stone-200 rounded-2xl flex flex-col items-center justify-center gap-3 p-8 text-stone-400 hover:border-emerald-400 hover:text-emerald-600 transition-colors cursor-pointer min-h-[200px]"
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-current">
            <i className="ri-add-line text-xl" />
          </div>
          <span className="text-sm font-medium">Yeni Kategori Ekle</span>
        </button>
      </div>

      {/* Modals */}
      {modal.type === "add" && (
        <CategoryFormModal mode="add" onSave={handleAdd} onClose={() => setModal({ type: "none" })} />
      )}
      {modal.type === "edit" && (
        <CategoryFormModal
          mode="edit"
          initial={{ title: modal.cat.title, desc: modal.cat.desc, slug: modal.cat.slug, icon: modal.cat.icon, count: modal.cat.count, image: modal.cat.image }}
          onSave={handleEdit}
          onClose={() => setModal({ type: "none" })}
        />
      )}
      {modal.type === "delete" && (
        <ConfirmModal
          title="Kategoriyi Sil"
          message={`"${modal.cat.title}" kategorisini silmek istediğinize emin misiniz? Bu kategorideki mekanlar etkilenebilir.`}
          confirmLabel="Evet, Sil"
          onConfirm={handleDelete}
          onCancel={() => setModal({ type: "none" })}
        />
      )}
    </div>
  );
}
